import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { ThemeProvider } from "./components/ThemeProvider";
import DailyBriefingPopup from "./components/DailyBriefingPopup";

// Semi-critical routes (content pages, lazy but likely needed)
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const CategoryArchive = lazy(() => import("./pages/CategoryArchive"));

// Lazy-loaded routes (non-critical)
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminArticles = lazy(() => import("./pages/AdminArticles"));
const AdminArticleEditor = lazy(() => import("./pages/AdminArticleEditor"));
const RegulatoryNews = lazy(() => import("./pages/RegulatoryNews"));
const PlaceholderPage = lazy(() => import("./pages/PlaceholderPage"));
const AdvertisePage = lazy(() => import("./pages/AdvertisePage"));
const BrokerOfTheMonth = lazy(() => import("./pages/BrokerOfTheMonth"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const DisclaimerPage = lazy(() => import("./pages/DisclaimerPage"));
const RegulationDirectory = lazy(() => import("./pages/RegulationDirectory"));
const RegulatorDetail = lazy(() => import("./pages/RegulatorDetail"));
const RegulatorCompare = lazy(() => import("./pages/RegulatorCompare"));
const GdprPage = lazy(() => import("./pages/GdprPage"));
const EditorialPolicyPage = lazy(() => import("./pages/EditorialPolicyPage"));
const MediaKitPage = lazy(() => import("./pages/MediaKitPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,    // 5 minutes
      gcTime: 30 * 60 * 1000,      // 30 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const placeholders = [
  // Brokers
  { path: "/brokers", title: "Broker Directory" },
  { path: "/brokers/reviews", title: "Broker Reviews" },
  { path: "/compare/brokers", title: "Broker Comparison" },
  { path: "/brokers/regulation-check", title: "Regulation Check" },
  { path: "/brokers/fee-analyzer", title: "Fee Analyzer" },
  { path: "/brokers/matchmaker", title: "Broker Matchmaker" },
  // Prop Firms
  { path: "/prop-firms", title: "Prop Firm Directory" },
  { path: "/prop-firms/reviews", title: "Prop Firm Reviews" },
  { path: "/compare/prop-firms", title: "Prop Firm Comparison" },
  { path: "/prop-firms/calculator", title: "Challenge Calculator" },
  { path: "/prop-firms/payouts", title: "Payout Tracker" },
  { path: "/prop-firms/applications", title: "Application Tracker" },
  // Safety
  { path: "/safety/scams", title: "Scam Alert Center" },
  { path: "/safety/lists", title: "Blacklist / Whitelist" },
  { path: "/safety/legal", title: "Legal Action Tracker" },
  { path: "/safety/due-diligence", title: "Due Diligence Tools" },
  { path: "/safety/withdrawals", title: "Withdrawal Speed Tracker" },
  { path: "/safety/insurance", title: "Insurance & Compensation" },
  // AI Tools
  { path: "/ai/assistant", title: "AI Trading Assistant" },
  { path: "/ai/sentiment", title: "Sentiment Analyzer" },
  { path: "/ai/tech", title: "Technology Tracker" },
  { path: "/ai/news", title: "Automated News" },
  { path: "/ai/future", title: "Future of Finance" },
  { path: "/ai/api", title: "API Access" },
  // Community
  { path: "/community/forums", title: "Trader Forums" },
  { path: "/community/reviews", title: "Verified Reviews" },
  { path: "/community/rankings", title: "User Rankings" },
  { path: "/community/live", title: "Live Market Analysis" },
  { path: "/community/events", title: "Events Calendar" },
  { path: "/education", title: "Education Center" },
  // Misc
  { path: "/contribute", title: "Write for Us" },
  
  { path: "/regulations", title: "Regulatory Watch" },
  { path: "/directory", title: "Entity Directory" },
  { path: "/regulations", title: "Regulatory Watch" },
  { path: "/directory", title: "Entity Directory" },
];

const App = () => (
  <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/news/:slug" element={<ArticleDetail />} />
              <Route path="/news" element={<CategoryArchive category="all" title="All News" description="Browse all articles across every category." />} />
              <Route path="/category/brokers" element={<CategoryArchive category="broker" title="Broker News" description="Reviews, alerts, and analysis covering forex & CFD brokers worldwide." />} />
              <Route path="/category/prop-firms" element={<CategoryArchive category="prop_firm" title="Prop Firm News" description="Funded trading firm news, challenges, and payout tracking." />} />
              <Route path="/category/regulation" element={<CategoryArchive category="regulation" title="Regulation" description="Regulatory warnings, fines, and policy changes across global markets." />} />
              <Route path="/category/exposes" element={<CategoryArchive category="expose" title="Exposés" description="In-depth investigations into scams, fraud, and unregulated entities." />} />
              <Route path="/category/reviews" element={<CategoryArchive category="review" title="Reviews" description="Hands-on, independently verified broker and prop firm reviews." />} />
              <Route path="/category/fintech" element={<CategoryArchive category="fintech" title="Fintech" description="Payment innovations, platforms, and emerging financial technology." />} />
              <Route path="/category/comparisons" element={<CategoryArchive category="comparison" title="Comparisons" description="Side-by-side comparisons of brokers and prop firms." />} />
              <Route path="/category/analysis" element={<CategoryArchive category="market_analysis" title="Market Analysis" description="Technical and fundamental analysis across currency markets." />} />
              <Route path="/category/awards" element={<CategoryArchive category="awards" title="Awards" description="Industry awards, rankings, and recognition." />} />
              <Route path="/category/events" element={<CategoryArchive category="event" title="Events" description="Expos, conferences, and webinars in the forex & fintech space." />} />
              <Route path="/advertise" element={<AdvertisePage />} />
              <Route path="/broker-of-the-month" element={<BrokerOfTheMonth />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/cookies" element={<CookiesPage />} />
              <Route path="/disclaimer" element={<DisclaimerPage />} />
              <Route path="/regulation" element={<RegulationDirectory />} />
              <Route path="/regulation/news" element={<RegulatoryNews />} />
              <Route path="/regulation/compare" element={<RegulatorCompare />} />
              <Route path="/regulation/:acronym" element={<RegulatorDetail />} />
              <Route path="/gdpr" element={<GdprPage />} />
              <Route path="/editorial-policy" element={<EditorialPolicyPage />} />
              <Route path="/media-kit" element={<MediaKitPage />} />
              <Route path="/sitemap" element={<SitemapPage />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/articles" element={<AdminArticles />} />
              <Route path="/admin/articles/new" element={<AdminArticleEditor />} />
              <Route path="/admin/articles/edit/:id" element={<AdminArticleEditor />} />
              {placeholders.map((p) => (
                <Route key={p.path} path={p.path} element={<PlaceholderPage title={p.title} />} />
              ))}
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <DailyBriefingPopup />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
